import { useEffect } from 'react';
import { Cache, Middleware, unstable_serialize } from 'swr';

const requestIdleCallback: (onIdle: () => void) => number =
  window['requestIdleCallback'] ||
  ((fn, ...args) => window.setTimeout(fn, 1, ...args));

const clearIdleCallback: (id: number) => void =
  window['clearIdleCallback'] || ((id) => window.clearTimeout(id));

// Combination of https://github.com/vercel/swr/pull/92 and https://gist.github.com/shuding/915d6844db74e131ece951a2eff0f320
// It is a two-step GC:
// 1. after # of inactive keys reaches [maxSize], we "schedule" these for deletion
// 2. after # of inactive keys reaches [maxSize] again, then we delete the previously scheduled keys and start over.
// Exposed as a factory function to allow granular GC behavior.
const createGCMiddleware = (maxSize: number): Middleware => {
  const keyCount: Record<string, number> = {};

  let inactiveKeys = new Set<string>();
  let inactiveKeysToBeDeleted = new Set<string>();

  const activateKey = (key: string) => {
    inactiveKeys.delete(key);
    inactiveKeysToBeDeleted.delete(key);
  };

  const deactivateKey = (key: string, cache: Cache) => {
    inactiveKeys.add(key);

    if (inactiveKeys.size >= maxSize) {
      inactiveKeysToBeDeleted.forEach((key) => cache.delete(key));
      inactiveKeysToBeDeleted = inactiveKeys;
      inactiveKeys = new Set();
    }
  };

  const hookActive = (key: string) =>
    requestIdleCallback(() => {
      keyCount[key] ??= 0;
      keyCount[key] += 1;

      if (keyCount[key] === 1) {
        activateKey(key);
      }
    });

  const hookInactive = (key: string, cache: Cache) =>
    requestIdleCallback(() => {
      keyCount[key] ??= 0;
      keyCount[key] -= 1;

      if (keyCount[key] <= 0) {
        delete keyCount[key];
        deactivateKey(key, cache);
      }
    });

  return (useSWRNext) => {
    const useEvictSWR: ReturnType<Middleware> = (key, fetcher, config) => {
      const serializedKey = unstable_serialize(key);

      useEffect(() => {
        const active = hookActive(serializedKey);
        return () => {
          clearIdleCallback(active);
          hookInactive(serializedKey, config.cache);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [serializedKey]);

      return useSWRNext(key, fetcher, config);
    };

    return useEvictSWR;
  };
};

export const globalGC = createGCMiddleware(150);
